import * as React from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import {
  Chip,
  TableCell,
  TableRow,
  Popper,
  Box,
  Paper,
  Typography,
  TextField,
  Divider,
  Stack,
  FormHelperText,
  FormControl,
  Checkbox,
  Tooltip,
  TableBody,
  IconButton,
} from '@mui/material';
import InputError, { InputErrorProps } from './InputError';
import { useAutoSubmitSignal } from 'hooks/autosubmit';
import { useFieldInfo } from 'contexts/FieldInfoContext';
import SearchIcon from '@mui/icons-material/Search';
import { getColumns, isSelectedOption, isSelectedValue, selectOption } from 'utils/option-utils';
import { useDebounce } from 'utils/utils';
import { validateField } from '../utils/validate-utils';
import { Option } from '../../../types/option';
import { useRef } from 'react';
import { useTranslator } from 'hooks/translator';
import DeleteIcon from '@mui/icons-material/Delete';
import { CancellableRequest, cloneRow, focusRef, PagedResults, reorder, rowIcon } from './select/common';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNotifier } from 'hooks/notification';
import { useApolloClient } from '@apollo/client/react/hooks/useApolloClient';
import { GET_CHOICES } from 'queries/task';
import { useFormInfo } from 'hooks/form';
import Report from "helpers/report";
import _ from 'lodash';
import { useDependants } from 'hooks/dependants';
import { DraggableProvided, Draggable, DragDropContext, Droppable } from '@hello-pangea/dnd';

function InputMultipleEntityFieldContent(props: InputErrorProps) {
  const { augProps, fieldProps, info } = useFieldInfo()
  const formInfo = useFormInfo()

  const client                            = useApolloClient()
  const pageSize                          = 100
  const filterRef                         = useRef<HTMLDivElement>(null);
  const [input, setInput, debouncedInput] = useDebounce("", 500)
  const [selected, setSelected]           = React.useState<Option[]>([])
  const [highlighted, highlight]          = React.useState<null | number>(null)
  const [anchorEl, setAnchorEl]           = React.useState<null | HTMLElement>(null);
  const [options, setOptions]             = React.useState<Option[]>(info.options || []);
  const [page, setPage]                   = React.useState<number>(0)
  const [loading, setLoading ]            = React.useState(false)
  const [filter, setFilter]               = React.useState<null | string>(null)
  const [showSelected, setShowSelected]   = React.useState<boolean>(true)
  const [request, setRequest]             = React.useState<null | CancellableRequest>(null)
  const [hasNextPage, setHasNextPage]     = React.useState(true)
  const dependantValues                   = useDependants()
  const { setFocus, setViewOpen }         = props
  const { signal }                        = useAutoSubmitSignal()
  const { t, translator }                 = useTranslator()
  const notifier                          = useNotifier()

  const open = Boolean(anchorEl);
  
  function openPopper(elem: HTMLElement | null) {
    setAnchorEl(elem)
    setViewOpen(true)
    setFocus(true)
    focusRef(filterRef)
  }

  function closePopper() {
    setFocus(false)
    setViewOpen(false)
    setAnchorEl(null)
    handleValidate(fieldProps.value)
  }

  React.useEffect(() => {
    if (_.isEmpty(dependantValues)) return

    setSelected([])
    setInput("")
    setOptions([])
    handleValidate([])
  }, [dependantValues])

  React.useEffect(() => {
    setSelected(fieldProps.value && Array.isArray(fieldProps.value) ? fieldProps.value : [])
    
    // translate options
    // selected = formatChoices(translator, selected, info.optionsKind, formInfo.processDefinition.key)
  }, [fieldProps.value]);

  React.useEffect(() => {
    if (!(open)){
      request?.cancel()
      setOptions([])
      setFilter(null)

      return;
    }
  
    if (input != debouncedInput) {
      request?.cancel()
    } else if (options.length == 0){
      loadOptions(input, 0)
    } else { // TODO: does this handle well with multiple select... I think not.
      loadOptions(debouncedInput, 0)
    } 
  }, [open, debouncedInput, input]);

  function onPageChange(_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) {
    if (newPage > page && !hasNextPage) return
    loadOptions(null, newPage)
    focusRef(filterRef)
  }

  function updateOptions(newOptions: Option[], filter: null | string) {
    setOptions(newOptions)
    setFilter(filter)
    setHasNextPage(!(newOptions.length < pageSize))
  }

  const loadOptions = async (newFilter: null | string, newPage?: number) => {
    request?.cancel()

    if (!open) return
    if (newFilter === filter && newPage === page) return

    const usedFilter = newFilter ?? filter
    const loadPage   = newPage ?? page

    setLoading(true)

    let cancelled = false
    const cancel = () => {
      if (!cancelled) {
        cancelled = true
        setLoading(false)
      }
    }

    function updateOptionsLocal(newOptions: Option[]) {
      if (!cancelled) {
        setPage(loadPage)
        updateOptions(newOptions, usedFilter)
      }
    }

    const variables = {
      ...formInfo.reference,
      path:   info.rpath,
      filter: usedFilter,
      start:  loadPage * pageSize,
      count:  pageSize,
      values: dependantValues
    }

    const newRequest = client.query({ query: GET_CHOICES, variables })
    const newCancellableRequest = {
      cancel: cancel,
      ...newRequest
    }
    setRequest(newCancellableRequest)

    newRequest
      .then(
        result => {
          if (!cancelled){
            console.log("loadOptions: searchQuery=%o, page=%o, variables=%o, result=%o", usedFilter, page, variables, result)
            const newOptions = result.data.choices
            updateOptionsLocal(newOptions)
          }
        },
        error => {
          if (!cancelled) {
            console.error("failed to get options: %o", error.message)
            const report = Report.from(error, translator, { category: Report.backend })
            report.addToNotifier(notifier)
          }
        }
      )
      .catch(reason => {
        if (!cancelled) {
          notifier.error("Could not retrieve more options")
          console.error("the frontend has an issue with retrieving options: " + reason)
        }
      })
      .finally(() => {
        if (!cancelled){
          setLoading(false)
        }
      })
  }

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    openPopper(e.currentTarget.parentElement);
    e.stopPropagation()
  }
  const handleDelete = (e: React.SyntheticEvent) => {
    setSelected([])
    augProps.setValue([])
    handleValidate([], e)
  }

  function handleBlur (e: any) {
    fieldProps.onBlur(e)
    handleValidate(fieldProps.value, e)
    setFocus(false)
    closePopper()
    signal()
  }

  function handleFocus(e: React.SyntheticEvent) {
    setFocus(true)
    fieldProps.onFocus({id: fieldProps.id, ...e})
  }

  function handleValidate(options: Option[], e?: React.SyntheticEvent) {
    const error = validateField("multiselect", fieldProps.required, e || null, options)
    augProps.setError(error)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    function select(option: Option) {
      e.stopPropagation()
      if (isSelectedOption(option, selected)) {
        selectItems(selected.filter(opt => opt !== option))
      } else {
        selectItems(selected.concat([option]))
      }
    }

    function highlightChange(nextIdx: Function, defaultIdx: number) {
      e.preventDefault()
      if (options.length === 0) return
      if (highlighted === null) {
        highlight(defaultIdx)
        e.stopPropagation()
        return
      }

      if (highlighted === -1) {
        highlight(defaultIdx)
        e.stopPropagation()
        return
      }

      highlight(nextIdx(highlighted))
    }

    switch (e.key) {
      case "Enter":
        e.preventDefault()
        if (loading) {
          e.stopPropagation()
          return
        }
        if (highlighted !== null) {
          select(options[highlighted])
          return
        }
        break
      case "Escape":
        e.preventDefault()
        handleValidate(fieldProps.value)
        closePopper()
        fieldProps.onBlur(e)
        break
      case "Tab":
      case "ArrowDown":
        highlightChange(
          (curIdx: number) => (curIdx + 1) % options.length,
          0
        )
        break
      case "ArrowUp":
        highlightChange(
          (curIdx: number) => curIdx === 0 ? options.length - 1 : (curIdx - 1) % options.length,
          options.length - 1
        )
        break
      default:
    }
  }

  function selectItems(newSelected: Option[]) {
    setSelected(newSelected)
    augProps.setValue(newSelected)
    handleValidate(newSelected)
  }

  function rowClickHandler(value: string) {
    return () => {
      if (isSelectedValue(value, selected)) { // deselect this option
        selectItems(selected.filter(option => option.value !== value))
        return
      }
      const option = selectOption(options, value)
      if (!option) {
        console.error("Couldn't find selected option with value %s", value)
        return
      }
      // not sure if should signal here
      signal()
      selectItems(selected.concat([option]))
    }
  }

  function filterTextChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInput(e.target.value)
  }

  function onDragEnd(result: any) {
    if (!result.destination) {
      return;
    }

    const newSelected = reorder(
      selected,
      result.source.index,
      result.destination.index
    );

    selectItems(newSelected)
  }

  function optionToRow(option: Option, index: number, provided: DraggableProvided, isSelected: boolean) {
    return <TableRow ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    key={option.value}
                    onClick={rowClickHandler(option.value)}
                    hover
                    sx={{ cursor: 'pointer', ...provided.draggableProps.style }}>
        {rowIcon(option.value, index, isSelected)}
        {Object.entries(option.tuple!).map(val => <TableCell key={[val[0], option.value, val[1]].join('_')}>{val[1]}</TableCell>)}
      </TableRow>
  }

  function makeDraggable(option: Option, index: number, isSelected: boolean) {
    return <Draggable key={index} draggableId={index.toString()} index={index} isDragDisabled={!isSelected}>
        {(provided, _snapshot) => optionToRow(option, index, provided, isSelected)}
      </Draggable>
  }

  const columns = getColumns(options.concat(selected))
  const total = hasNextPage
      ? -1
      : page * 100 + options.length
  const resultProps = {
      loading: loading,
      empty: options.length === 0 && selected.length === 0,
      page: page,
      onPageChange: onPageChange,
      total: total,
      header: <TableRow><TableCell/>{columns.map(col => <TableCell key={col}>{col}</TableCell>)}</TableRow>,
      body: <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={info.rpath + '-drop'}
                  renderClone={(provided, _snapshot, rubic) => cloneRow(selected[rubic.source.index], rubic.source.index, provided)}>
          {(provided, _snapshot) =>
            <TableBody {...provided.droppableProps} ref={provided.innerRef}>
              {selected.filter(_ => showSelected)
                .map((option, index) => makeDraggable(option, index, true))
                .concat(!showSelected ? [] : [
                  <Draggable key="divider-drag" draggableId='divider' index={selected.length} isDragDisabled={true}>
                    {(provided, _snapshot) =>
                      <TableRow key="divider"
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                sx={{ backgroundColor: '#f5f5f5', ...provided.draggableProps.style }}>
                        <TableCell colSpan={columns.length + 1}/>
                      </TableRow>}
                  </Draggable>
                ]).concat(options
                  .filter(option => !selected.find(e => e.value === option.value))
                  .map((option, index) => makeDraggable(option, index + selected.length + 1, false)))}
              {provided.placeholder}
            </TableBody>
          }
        </Droppable>
      </DragDropContext>
    }

  const error = augProps.error
  const selectedExists = selected.length !== 0
  const extraProps = selectedExists ? { onDelete: handleDelete} : {}
  const textColor = error ? 'error' : null

  const selectedLabel = !selectedExists
      ? <Typography display='inline' color={textColor || '#6b778c'}>{t("select.nothingselected")}</Typography>
      : <Typography display='inline' color={textColor || 'default'}>{t('select.list', { variables: { nr: selected.length } })}</Typography>
  const label = info.inMultiple
    ? selectedLabel
    : <Stack direction='row' spacing='16px'>
        <Typography color={textColor || '#6b778c'} display='inline'>
          {fieldProps.label + (fieldProps.required ? ' *' : '')}
        </Typography>
        <Divider orientation="vertical" flexItem />
        {selectedLabel}
      </Stack>

  // TODO: check if zIndex 1200 is ideal or should be lower
  return <>
    <FormControl margin={info.inMultiple ? "none" : "normal"}
                 error={Boolean(error)}
                 onFocus={(e) => openPopper(e.currentTarget)}
                 sx={{minWidth: "100%", width: 'max-content'}}>
      <Chip sx={{ minHeight: info.inMultiple ? '40px' : '56px', borderRadius: '5px', justifyContent: 'left' }}
            id={fieldProps.id}
            clickable
            variant='outlined'
            onClick={handleClick}
            label={label}
            color={error ? 'error' : 'default'}
            deleteIcon={<DeleteIcon onMouseDown={(e) => e.preventDefault()} /* prevent receiving focus when clicking delete button*/ />}
            {...extraProps}
            />
      {info.inMultiple ? null : <FormHelperText>{error || ''}</FormHelperText>}
    </FormControl>
    <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{zIndex: 1200}}>
      <ClickAwayListener onClickAway={handleBlur}>
        <Paper elevation={4} sx={{padding: '5 px'}}>
          <Box sx={{ display: 'flex', alignItems: 'center', p: 1, pl: 1.5 }}>
            <SearchIcon sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
            <TextField
                id='filter-options'
                variant='standard'
                hiddenLabel
                // onBlur={handleBlur}
                onFocus={handleFocus}
                value={input}
                onChange={filterTextChange}
                onKeyDown={handleKeyDown}
                inputRef={filterRef}/>
            <Box sx={{paddingLeft: "16px", display: 'flex', flexGrow: 1, justifyContent: 'right'}}>
              <Tooltip placement='right' title={t('select.showselected')}>
                <IconButton onClick={_ => setShowSelected(!showSelected)}>
                  {showSelected ? <Visibility/> : <VisibilityOff/>}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Divider/>
          <Box sx={{ p: 0 }}>
            <PagedResults {...resultProps}/>
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  </>
}

const InputMultipleEntityField = () => (
  <InputError>
    {/* @ts-ignore */}
    <InputMultipleEntityFieldContent />
  </InputError>
)

export default InputMultipleEntityField


